<template>
  <div v-if="show" class="agent-modal-backdrop" @click.self="close">
    <div class="agent-modal agent-kb-modal" role="dialog" aria-modal="true" aria-labelledby="agentKbTitle">
      <div class="agent-modal-head">
        <div>
          <span class="agent-modal-kicker">Knowledge Base</span>
          <h5 id="agentKbTitle">Knowledge Base Lisensi</h5>
          <p v-if="license" class="agent-modal-subtitle">
            {{ license.license_name || "Tanpa nama" }} · {{ license.license_phone_number || "-" }}
          </p>
        </div>
        <button type="button" class="agent-modal-close" aria-label="Tutup" @click="close">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="agent-modal-body">
        <div v-if="!license" class="agent-kb-state is-error">
          Lisensi belum dipilih. Tutup modal lalu pilih lisensi terlebih dahulu.
        </div>

        <template v-else>
          <div class="agent-kb-summary">
            <div>
              <strong>{{ total }} dari {{ limit }} KB</strong>
              <span>Dokumen Knowledge Base digunakan bersama oleh semua agent AI pada lisensi ini.</span>
            </div>
            <agent-button
              variant="primary"
              size="sm"
              icon="fa fa-plus"
              :disabled="limitReached || loading"
              @click="openUpload"
            >
              Tambah Knowledge Base
            </agent-button>
          </div>

          <div v-if="limitReached" class="agent-kb-limit-note">
            Lisensi ini sudah menggunakan {{ limit }} dari {{ limit }} Knowledge Base. Hapus salah satu dokumen sebelum menambah yang baru.
          </div>

          <div v-if="uploadSuccess" class="agent-modal-note is-success">
            <i class="fa fa-check-circle"></i> {{ uploadSuccess }}
          </div>
          <div v-if="actionSuccess" class="agent-modal-note is-success">
            <i class="fa fa-check-circle"></i> {{ actionSuccess }}
          </div>
          <div v-if="jobPollError" class="agent-modal-note is-warning">
            <i class="fa fa-exclamation-triangle"></i> {{ jobPollError }}
          </div>

          <div v-if="confirmRow" ref="kbConfirm" class="agent-kb-confirm">
            <div class="agent-kb-confirm-copy">
              <strong>Hapus Knowledge Base?</strong>
              <span>
                Dokumen <b>{{ confirmRow.file_name || "ini" }}</b> akan dihapus dari lisensi ini.
              </span>
              <small v-if="deleteError">{{ deleteError }}</small>
            </div>
            <div class="agent-kb-confirm-actions">
              <agent-button
                variant="secondary"
                size="sm"
                :disabled="!!deletingId"
                @click="cancelDelete"
              >
                Batal
              </agent-button>
              <agent-button
                variant="primary"
                size="sm"
                icon="fa fa-trash-o"
                :loading="!!deletingId"
                :disabled="!!deletingId"
                @click="confirmDelete"
              >
                Ya, hapus
              </agent-button>
            </div>
          </div>

          <div v-if="uploadOpen" class="agent-kb-upload">
            <div class="agent-kb-upload-head">
              <strong>Tambah Knowledge Base</strong>
              <button type="button" class="btn btn-link text-muted" @click="cancelUpload">Batal</button>
            </div>

            <div v-show="!files.length" class="agent-kb-file-picker">
              <file-upload
                ref="upload"
                class="btn btn-outline-secondary"
                :post-action="uploadUrl"
                :headers="uploadHeaders"
                :data="uploadData"
                :multiple="false"
                :maximum="1"
                :accept="accept"
                :extensions="extensions"
                v-model="files"
                @input-filter="inputFilter"
                @input-file="inputFile"
              >
                <i class="fa fa-upload"></i> Pilih file
              </file-upload>
              <small>Format: PDF, DOC, DOCX, TXT. Maksimal 10 MB.</small>
            </div>

            <div v-if="files.length" class="agent-kb-selected-file">
              <div>
                <i class="fa fa-file-text-o"></i>
                <strong>{{ files[0].name }}</strong>
                <small>{{ formatSize(files[0].size) }}</small>
              </div>
              <button type="button" class="btn btn-link text-danger" :disabled="uploading" @click="cancelUpload">
                Hapus
              </button>
            </div>

            <div v-if="uploading" class="agent-kb-progress">
              <div class="agent-kb-progress-bar" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <div v-if="uploadError" class="agent-modal-note is-error">{{ uploadError }}</div>

            <div class="agent-kb-upload-actions">
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="uploading" @click="cancelUpload">
                Batal
              </button>
              <agent-button
                variant="primary"
                size="sm"
                :icon="uploading ? '' : 'fa fa-upload'"
                :loading="uploading"
                :disabled="!files.length"
                @click="startUpload"
              >
                {{ uploading ? "Mengunggah..." : "Upload" }}
              </agent-button>
            </div>
          </div>

          <div v-if="loading" class="agent-kb-state">
            <i class="fa fa-spinner fa-spin"></i> Memuat Knowledge Base...
          </div>

          <div v-else-if="loadError" class="agent-kb-state is-error">
            <span>{{ loadError }}</span>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="load">Coba lagi</button>
          </div>

          <div v-else-if="!rows.length" class="agent-kb-empty">
            <i class="fa fa-book"></i>
            <strong>Belum ada Knowledge Base untuk lisensi ini.</strong>
            <span>Tambahkan dokumen agar AI dapat menggunakannya.</span>
          </div>

          <div v-else class="agent-kb-list">
            <div v-for="row in rows" :key="row.id" class="agent-kb-row">
              <div class="agent-kb-row-main">
                <span class="agent-kb-file-icon"><i class="fa fa-file-text-o"></i></span>
                <div>
                  <strong>{{ row.file_name || "Dokumen tanpa nama" }}</strong>
                  <small>
                    {{ formatDate(row.create_datetime) }}
                    <span v-if="row.file_type"> · {{ row.file_type }}</span>
                  </small>
                  <div v-if="rowJob(row)" class="agent-kb-row-progress">
                    <div class="agent-kb-row-progress-head">
                      <span>{{ rowJobMeta(row).label }}</span>
                      <em>{{ rowJobPercentLabel(row) }}</em>
                    </div>
                    <div class="agent-kb-progress is-inline">
                      <div
                        class="agent-kb-progress-bar"
                        :class="'is-' + rowJobMeta(row).tone"
                        :style="{ width: rowJobPercent(row) + '%' }"
                      ></div>
                    </div>
                    <small class="agent-kb-row-progress-meta">
                      {{ formatNumber(rowJobCompleted(row)) }} dari {{ formatNumber(rowJobTotal(row)) }} bagian selesai
                      <template v-if="rowJobFailed(row)"> · {{ formatNumber(rowJobFailed(row)) }} gagal</template>
                    </small>
                    <small v-if="rowJobErrorText(row)" class="agent-kb-row-progress-error">
                      {{ rowJobErrorText(row) }}
                    </small>
                  </div>
                  <small v-if="jobErrors[String(row.id)]" class="agent-kb-row-progress-error">
                    {{ jobErrors[String(row.id)] }}
                  </small>
                </div>
              </div>
              <div class="agent-kb-row-actions">
                <span class="agent-kb-status" :class="'is-' + rowStatusMeta(row).tone">
                  {{ rowStatusMeta(row).label }}
                </span>
                <button type="button" class="btn btn-link btn-sm" @click="showDetail(row)">Detail</button>
                <a v-if="row.file_url" :href="row.file_url" target="_blank" rel="noopener" class="btn btn-link btn-sm">
                  Lihat file
                </a>
                <button
                  type="button"
                  class="btn btn-link btn-sm text-danger"
                  :disabled="deletingId === row.id"
                  @click="requestDelete(row)"
                >
                  <i v-if="deletingId === row.id" class="fa fa-spinner fa-spin"></i>
                  <i v-else class="fa fa-trash-o"></i>
                  Hapus
                </button>
              </div>
            </div>
          </div>

          <div v-if="detailRow" class="agent-kb-detail">
            <div class="agent-kb-detail-head">
              <strong>Detail dokumen</strong>
              <button type="button" class="btn btn-link btn-sm" @click="detailRow = null">Tutup</button>
            </div>
            <div><span>Nama file</span><strong>{{ detailRow.file_name }}</strong></div>
            <div><span>Status</span><strong>{{ detailStatusMeta.label }}</strong></div>
            <div><span>Waktu upload</span><strong>{{ formatDate(detailRow.create_datetime) }}</strong></div>
            <div v-if="detailRow.message"><span>Keterangan</span><strong>{{ detailRow.message }}</strong></div>

            <div v-if="detailJob" class="agent-kb-detail-job">
              <div class="agent-kb-detail-job-head">
                <strong>Pemrosesan Knowledge Base</strong>
                <span class="agent-kb-status" :class="'is-' + detailJobMeta.tone">{{ detailJobMeta.label }}</span>
              </div>
              <div class="agent-kb-progress">
                <div
                  class="agent-kb-progress-bar"
                  :class="'is-' + detailJobMeta.tone"
                  :style="{ width: detailJobPercent + '%' }"
                ></div>
              </div>
              <div class="agent-kb-detail-job-percent">
                {{ detailJobPercentLabel }} selesai ·
                {{ formatNumber(detailJob.progress.completed) }} dari
                {{ formatNumber(detailJob.progress.total) }} bagian
              </div>
              <div class="agent-kb-detail-counts">
                <span>Selesai <strong>{{ formatNumber(detailJob.progress.completed) }}</strong></span>
                <span>Proses <strong>{{ formatNumber(detailJob.progress.processing) }}</strong></span>
                <span>Menunggu <strong>{{ formatNumber(detailJob.progress.queued) }}</strong></span>
                <span>Gagal <strong>{{ formatNumber(detailJob.progress.failed) }}</strong></span>
              </div>
              <p v-if="detailJobError" class="agent-kb-detail-error">
                <i class="fa fa-exclamation-circle"></i> {{ detailJobError }}
              </p>

              <div class="agent-kb-detail-chunks">
                <div class="agent-kb-detail-chunks-head">
                  <strong>Status bagian</strong>
                  <button
                    type="button"
                    class="btn btn-link btn-sm"
                    :disabled="detailChunksLoading"
                    @click="loadDetailChunks(detailChunksPage, true)"
                  >
                    <i class="fa" :class="detailChunksLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
                    Perbarui
                  </button>
                </div>
                <div v-if="detailChunksError" class="agent-modal-note is-error">{{ detailChunksError }}</div>
                <div v-else-if="detailChunksLoading && !detailChunks.length" class="agent-kb-detail-muted">
                  <i class="fa fa-spinner fa-spin"></i> Memuat status bagian...
                </div>
                <div v-else-if="!detailChunks.length" class="agent-kb-detail-muted">
                  Status bagian belum tersedia.
                </div>
                <div v-else class="agent-kb-chunk-list">
                  <div
                    v-for="chunk in detailChunks"
                    :key="chunk.chunk_index"
                    class="agent-kb-chunk"
                    :class="'is-' + detailChunkMeta(chunk).tone"
                  >
                    <div class="agent-kb-chunk-head">
                      <strong>Bagian {{ chunk.sequence }}</strong>
                      <span class="agent-kb-status" :class="'is-' + detailChunkMeta(chunk).tone">
                        {{ detailChunkMeta(chunk).label }}
                      </span>
                    </div>
                    <small v-if="chunk.preview" class="agent-kb-chunk-preview">{{ chunk.preview }}</small>
                    <em>{{ formatNumber(chunk.char_count) }} karakter · {{ formatNumber(chunk.attempts) }} percobaan</em>
                    <small v-if="detailChunkErrorText(chunk)" class="agent-kb-chunk-error">
                      {{ detailChunkErrorText(chunk) }}
                    </small>
                  </div>
                </div>
                <div v-if="detailChunksTotalPages > 1" class="agent-kb-chunk-pager">
                  <button
                    type="button"
                    class="btn btn-link btn-sm"
                    :disabled="detailChunksLoading || detailChunksPage <= 1"
                    @click="loadDetailChunks(detailChunksPage - 1, true)"
                  >
                    Sebelumnya
                  </button>
                  <span>Halaman {{ detailChunksPage }} / {{ detailChunksTotalPages }}</span>
                  <button
                    type="button"
                    class="btn btn-link btn-sm"
                    :disabled="detailChunksLoading || detailChunksPage >= detailChunksTotalPages"
                    @click="loadDetailChunks(detailChunksPage + 1, true)"
                  >
                    Berikutnya
                  </button>
                </div>
              </div>
            </div>
            <div v-else-if="detailJobLoading" class="agent-kb-detail-muted">
              <i class="fa fa-spinner fa-spin"></i> Memeriksa status antrean...
            </div>
            <div v-else-if="detailChunksError" class="agent-modal-note is-error">{{ detailChunksError }}</div>
            <div v-else-if="detailJobMissing" class="agent-kb-detail-legacy">
              Dokumen ini dibuat sebelum monitoring antrean tersedia. Isi Knowledge Base tetap dapat digunakan
              seperti biasa.
            </div>
          </div>
        </template>
      </div>

      <div class="agent-modal-foot">
        <button type="button" class="btn btn-outline-secondary" @click="close">Tutup</button>
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from "vue-upload-component";
import { APICore } from "@/utils/servicesApi";
import { getXAuthToken } from "@/utils/auth";
import { formatSizes } from "@/utils/helper";
import {
  createKbJobMonitor,
  formatKbPercent,
  isKbJobActive,
  kbChunkStatusMeta,
  kbErrorHttpStatus,
  kbJobStatusMeta,
  normalizeKbJob,
  safeKbChunkError,
  safeKbJobError,
  shouldApplyKbJob
} from "@/utils/knowledgeBaseQueue";
import { friendlyError } from "../utils";
import AgentButton from "./AgentButton.vue";

export default {
  name: "KnowledgeBaseModal",
  components: {
    FileUpload,
    AgentButton
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    license: {
      type: Object,
      default: null
    },
    limit: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      rows: [],
      total: 0,
      loading: false,
      loadError: "",
      uploadOpen: false,
      uploadSuccess: "",
      uploadError: "",
      actionSuccess: "",
      uploading: false,
      files: [],
      uploadData: {
        license_id: "",
        message_type: "document",
        is_extract_document: 1
      },
      accept: "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain",
      extensions: "pdf,doc,docx,txt",
      uploadUrl: process.env.VUE_APP_BASE_API + APICore() + "/omni/only-upload-filev3",
      deletingId: "",
      confirmRow: null,
      deleteError: "",
      detailRow: null,

      // Monitoring antrean pemrosesan Knowledge Base.
      jobs: {},
      jobLoading: {},
      jobMissing: {},
      jobErrors: {},
      jobPollError: "",

      detailJob: null,
      detailJobLoading: false,
      detailJobMissing: false,
      detailJobRequestId: 0,

      detailChunks: [],
      detailChunksLoading: false,
      detailChunksError: "",
      detailChunksPage: 1,
      detailChunksLimit: 10,
      detailChunksTotal: 0,
      detailChunksTotalPages: 0,
      detailChunksJobId: "",
      detailChunksRequestId: 0
    };
  },
  computed: {
    uploadHeaders() {
      return {
        "X-Auth-Token": "Bearer " + getXAuthToken()
      };
    },
    limitReached() {
      return this.total >= this.limit;
    },
    uploadProgress() {
      if (!this.files.length || !this.files[0].progress) return 0;
      return parseFloat(this.files[0].progress) || 0;
    },
    detailStatusMeta() {
      if (this.detailJob) return kbJobStatusMeta(this.detailJob.status);
      return this.rowStatusMeta(this.detailRow || {});
    },
    detailJobMeta() {
      return kbJobStatusMeta(this.detailJob && this.detailJob.status);
    },
    detailJobPercent() {
      return this.detailJob ? Number(this.detailJob.progress.percent) || 0 : 0;
    },
    detailJobPercentLabel() {
      return formatKbPercent(this.detailJobPercent);
    },
    detailJobError() {
      return safeKbJobError(this.detailJob);
    }
  },
  watch: {
    show(value) {
      if (value) {
        this.uploadSuccess = "";
        this.uploadError = "";
        this.actionSuccess = "";
        this.uploadOpen = false;
        this.detailRow = null;
        this.confirmRow = null;
        this.deleteError = "";
        this.jobPollError = "";
        this.resetDetailState();
        this.load();
      } else {
        this.stopMonitoring();
      }
    },
    license: {
      deep: true,
      handler() {
        if (this.show) {
          this.confirmRow = null;
          this.deleteError = "";
          this.actionSuccess = "";
          this.resetJobState();
          this.load();
        }
      }
    }
  },
  created() {
    this.monitor = createKbJobMonitor({
      intervalMs: 3000,
      fetchJob: (jobId) => this.$store.dispatch("aiKey/knowledgeBaseJobDetail", { job_id: jobId }),
      onUpdate: (job) => this.applyJobUpdate(job),
      onError: () => {
        this.jobPollError = "Pembaruan status antrean tertunda. Sistem mencoba lagi secara otomatis.";
      }
    });
  },
  beforeDestroy() {
    this.stopMonitoring();
  },
  methods: {
    close() {
      this.stopMonitoring();
      this.uploadOpen = false;
      this.uploadSuccess = "";
      this.uploadError = "";
      this.actionSuccess = "";
      this.confirmRow = null;
      this.deleteError = "";
      this.jobPollError = "";
      this.resetDetailState();
      this.$emit("close");
    },
    async load() {
      if (!this.license || !this.license.id) {
        this.rows = [];
        this.total = 0;
        this.loadError = "Lisensi belum dipilih.";
        this.resetJobState();
        return;
      }
      this.loading = true;
      this.loadError = "";
      this.uploadData.license_id = this.license.id;
      try {
        const response = await this.$store.dispatch("aiKey/historyListKB", {
          page: 0,
          perPage: 50,
          columnFilters: { license_id: this.license.id }
        });
        this.rows = (response.data && response.data.data) || [];
        this.total = Number(response.data && response.data.totalRecords) || this.rows.length;
        this.refreshRowJobs();
      } catch (error) {
        this.rows = [];
        this.total = 0;
        this.loadError = friendlyError(error, "Knowledge Base belum berhasil dimuat. Silakan coba lagi.");
        this.resetJobState();
      } finally {
        this.loading = false;
      }
    },
    openUpload() {
      if (this.limitReached) return;
      this.uploadOpen = true;
      this.uploadSuccess = "";
      this.uploadError = "";
      this.actionSuccess = "";
      this.files = [];
      this.uploadData.license_id = this.license.id;
      this.$nextTick(() => {
        if (this.$refs.upload && this.$refs.upload.clear) {
          this.$refs.upload.clear();
        }
      });
    },
    cancelUpload() {
      if (this.$refs.upload && this.$refs.upload.clear) {
        this.$refs.upload.clear();
      }
      this.files = [];
      this.uploading = false;
      this.uploadError = "";
      this.uploadOpen = false;
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!this.license) {
          this.uploadError = "Lisensi belum dipilih.";
          return prevent();
        }
        if (this.limitReached) {
          this.uploadError = "Lisensi ini sudah menggunakan 5 dari 5 Knowledge Base.";
          return prevent();
        }
        if (newFile.size > 10 * 1024 * 1024) {
          this.uploadError = "Ukuran file maksimal 10 MB.";
          return prevent();
        }
        if (newFile.size < 1024) {
          this.uploadError = "File terlalu kecil.";
          return prevent();
        }
        this.uploadError = "";
      }
    },
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        if (newFile.error && !oldFile.error) {
          this.uploading = false;
          this.uploadError = friendlyError(
            { message: (newFile.response && newFile.response.msg) || newFile.error },
            "Dokumen belum berhasil diunggah. Silakan coba lagi."
          );
        }
        if (newFile.success && !oldFile.success) {
          this.uploading = false;
          const response = newFile.response || {};
          const data = response.data || {};
          if ((response.status && response.status !== 200) || !data.kb_file_id || data.text_error) {
            this.uploadError = data.text_error
              ? "Dokumen tidak dapat diproses. Pastikan file berisi teks yang dapat dibaca."
              : "Dokumen belum berhasil diproses. Silakan coba lagi.";
            return;
          }
          const jobId = data.job_id || "";
          const uploadedFileId = Number(data.kb_file_id || data.file_id || data.history_id) || 0;
          this.uploadSuccess = "Dokumen berhasil diunggah. Pemrosesan Knowledge Base sedang berjalan.";
          this.uploadOpen = false;
          this.files = [];
          this.load().then(() => {
            if (!this.show) return;
            if (jobId) {
              if (uploadedFileId) this.$set(this.jobMissing, String(uploadedFileId), false);
              this.monitor.track(jobId);
            }
          });
          this.$emit("changed");
        }
      }
    },
    startUpload() {
      if (!this.files.length || this.uploading) return;
      this.uploading = true;
      this.uploadError = "";
      this.$nextTick(() => {
        if (this.$refs.upload) {
          this.$refs.upload.active = true;
        } else {
          this.uploading = false;
          this.uploadError = "File belum siap diunggah. Coba pilih file lagi.";
        }
      });
    },
    requestDelete(row) {
      if (this.deletingId) return;
      this.confirmRow = row;
      this.deleteError = "";
      this.actionSuccess = "";
      this.uploadSuccess = "";
      this.$nextTick(() => {
        const el = this.$refs.kbConfirm;
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
    cancelDelete() {
      if (this.deletingId) return;
      this.confirmRow = null;
      this.deleteError = "";
    },
    async confirmDelete() {
      if (!this.confirmRow || this.deletingId) return;
      const row = this.confirmRow;
      this.deletingId = row.id;
      this.deleteError = "";
      try {
        await this.$store.dispatch("aiKey/deleteKB", {
          id: parseInt(row.id, 10),
          license_id: this.license.id
        });
        const jobKey = String(row.id);
        const deletedJob = this.jobs[jobKey];
        if (deletedJob && deletedJob.job_id) this.monitor.untrack(deletedJob.job_id);
        this.$delete(this.jobs, jobKey);
        this.$delete(this.jobLoading, jobKey);
        this.$delete(this.jobMissing, jobKey);
        this.$delete(this.jobErrors, jobKey);
        if (this.detailRow && this.detailRow.id === row.id) this.resetDetailState();
        this.confirmRow = null;
        this.actionSuccess = "Dokumen berhasil dihapus.";
        await this.load();
        this.$emit("changed");
      } catch (error) {
        this.deleteError = friendlyError(
          error,
          "Dokumen belum berhasil dihapus. Silakan coba lagi."
        );
      } finally {
        this.deletingId = "";
      }
    },
    showDetail(row) {
      this.detailRow = row;
      this.loadDetailJob(row);
    },
    formatNumber(value) {
      const number = Number(value);
      if (!isFinite(number)) return "0";
      return String(Math.round(number)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    stopMonitoring() {
      if (this.monitor) this.monitor.untrackAll();
    },
    resetJobState() {
      this.stopMonitoring();
      this.jobs = {};
      this.jobLoading = {};
      this.jobMissing = {};
      this.jobErrors = {};
      this.jobPollError = "";
      this.resetDetailState();
    },
    resetDetailState() {
      this.detailRow = null;
      this.detailJob = null;
      this.detailJobLoading = false;
      this.detailJobMissing = false;
      this.detailJobRequestId++;
      this.resetDetailChunks();
    },
    resetDetailChunks() {
      this.detailChunks = [];
      this.detailChunksError = "";
      this.detailChunksPage = 1;
      this.detailChunksTotal = 0;
      this.detailChunksTotalPages = 0;
      this.detailChunksJobId = "";
      this.detailChunksLoading = false;
      this.detailChunksRequestId++;
    },
    rowJob(row) {
      if (!row) return null;
      return this.jobs[String(row.id)] || null;
    },
    rowJobMeta(row) {
      const job = this.rowJob(row);
      return kbJobStatusMeta(job && job.status);
    },
    rowJobPercent(row) {
      const job = this.rowJob(row);
      return job ? Number(job.progress.percent) || 0 : 0;
    },
    rowJobPercentLabel(row) {
      return formatKbPercent(this.rowJobPercent(row));
    },
    rowJobCompleted(row) {
      const job = this.rowJob(row);
      return job ? job.progress.completed : 0;
    },
    rowJobTotal(row) {
      const job = this.rowJob(row);
      return job ? job.progress.total : 0;
    },
    rowJobFailed(row) {
      const job = this.rowJob(row);
      return job ? job.progress.failed : 0;
    },
    rowJobErrorText(row) {
      return safeKbJobError(this.rowJob(row));
    },
    rowStatusMeta(row) {
      const job = this.rowJob(row);
      if (job) return kbJobStatusMeta(job.status);
      const status = String((row && row.status) || "").toLowerCase();
      if (status === "success") return { label: "Selesai", tone: "success" };
      if (status === "processing") return { label: "Sedang diproses", tone: "processing" };
      if (status === "failed") return { label: "Gagal", tone: "danger" };
      if (status === "pending") return { label: "Menunggu", tone: "muted" };
      return { label: status || "Menunggu", tone: "muted" };
    },
    detailChunkMeta(chunk) {
      return kbChunkStatusMeta(chunk && chunk.status);
    },
    detailChunkErrorText(chunk) {
      return safeKbChunkError(chunk);
    },
    applyJob(job) {
      if (!job || !job.file_id) return false;
      const key = String(job.file_id);
      const previous = this.jobs[key] || null;
      if (!shouldApplyKbJob(previous, job)) return false;
      if (previous && previous.job_id && previous.job_id !== job.job_id) {
        this.monitor.untrack(previous.job_id);
      }
      this.$set(this.jobs, key, job);
      this.$set(this.jobMissing, key, false);
      this.$set(this.jobErrors, key, "");
      if (this.detailRow && Number(this.detailRow.id) === Number(job.file_id)) {
        this.detailJob = job;
      }
      return true;
    },
    applyJobUpdate(job) {
      if (!job || !this.show) return;
      this.jobPollError = "";
      const key = String(job.file_id);
      const previous = this.jobs[key] || null;
      const applied = this.applyJob(job);
      if (!applied) return;
      if (!this.detailRow || Number(this.detailRow.id) !== Number(job.file_id)) return;
      this.detailJob = job;
      if (this.detailChunksJobId !== job.job_id) {
        this.loadDetailChunks(1);
      } else if (previous && isKbJobActive(previous.status) && !isKbJobActive(job.status)) {
        this.loadDetailChunks(1, true);
      }
    },
    async resolveRowJob(row) {
      if (!row || !this.license || !this.license.id) return;
      const key = String(row.id);
      if (this.jobLoading[key]) return;
      const licenseId = this.license.id;
      this.$set(this.jobLoading, key, true);
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseLatestJob", {
          file_id: row.id,
          license_id: licenseId
        });
        if (!this.show) return;
        if (!this.license || String(this.license.id) !== String(licenseId)) return;
        const job = normalizeKbJob(response.data);
        if (!job || !job.file_id) return;
        this.applyJob(job);
        if (isKbJobActive(job.status)) this.monitor.track(job.job_id);
      } catch (error) {
        if (!this.show) return;
        if (!this.license || String(this.license.id) !== String(licenseId)) return;
        if (kbErrorHttpStatus(error) === 404) {
          this.$set(this.jobMissing, key, true);
          this.$set(this.jobErrors, key, "");
        } else {
          this.$set(this.jobErrors, key, "Status antrean belum dapat diperiksa.");
        }
      } finally {
        this.$set(this.jobLoading, key, false);
      }
    },
    refreshRowJobs() {
      this.rows.forEach((row) => {
        this.resolveRowJob(row);
      });
    },
    async loadDetailJob(row) {
      if (!row || !this.license || !this.license.id) return;
      const requestId = ++this.detailJobRequestId;
      this.resetDetailChunks();
      this.detailJobMissing = false;
      this.detailJob = this.jobs[String(row.id)] || null;
      if (this.detailJob) {
        this.loadDetailChunks(1);
        return;
      }
      const licenseId = this.license.id;
      this.detailJobLoading = true;
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseLatestJob", {
          file_id: row.id,
          license_id: licenseId
        });
        if (requestId !== this.detailJobRequestId || !this.show) return;
        if (!this.license || String(this.license.id) !== String(licenseId)) return;
        const job = normalizeKbJob(response.data);
        if (!job) return;
        this.applyJob(job);
        this.detailJob = job;
        if (isKbJobActive(job.status)) this.monitor.track(job.job_id);
        this.loadDetailChunks(1);
      } catch (error) {
        if (requestId !== this.detailJobRequestId || !this.show) return;
        if (kbErrorHttpStatus(error) === 404) {
          this.detailJobMissing = true;
        } else {
          this.detailChunksError = friendlyError(error, "Status antrean belum berhasil dimuat.");
        }
      } finally {
        if (requestId === this.detailJobRequestId) {
          this.detailJobLoading = false;
        }
      }
    },
    async loadDetailChunks(page, force) {
      if (!this.detailJob || !this.detailJob.job_id || this.detailChunksLoading) return;
      const targetPage = Math.max(Number(page) || 1, 1);
      if (!force && targetPage === this.detailChunksPage && this.detailChunks.length) return;
      const jobId = this.detailJob.job_id;
      const requestId = ++this.detailChunksRequestId;
      this.detailChunksLoading = true;
      this.detailChunksError = "";
      this.detailChunksJobId = jobId;
      try {
        const response = await this.$store.dispatch("aiKey/knowledgeBaseJobChunks", {
          job_id: jobId,
          page: targetPage,
          limit: this.detailChunksLimit
        });
        if (requestId !== this.detailChunksRequestId) return;
        if (!this.detailJob || this.detailJob.job_id !== jobId) return;
        const data = response.data || {};
        const pagination = data.pagination || {};
        this.detailChunks = data.items || [];
        this.detailChunksPage = Number(pagination.page) || targetPage;
        this.detailChunksTotal = Number(pagination.total) || 0;
        this.detailChunksTotalPages = Number(pagination.total_pages) || 0;
      } catch (error) {
        if (requestId !== this.detailChunksRequestId) return;
        this.detailChunks = [];
        this.detailChunksTotal = 0;
        this.detailChunksTotalPages = 0;
        this.detailChunksError = friendlyError(error, "Status bagian pemrosesan belum berhasil dimuat.");
      } finally {
        if (requestId === this.detailChunksRequestId) {
          this.detailChunksLoading = false;
        }
      }
    },
    statusLabel(status) {
      if (status === "success") return "Selesai";
      if (status === "processing") return "Sedang diproses";
      if (status === "failed") return "Gagal";
      if (status === "pending") return "Menunggu";
      return status || "Menunggu";
    },
    formatDate(value) {
      const timestamp = Number(value);
      if (!timestamp) return "-";
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatSize(value) {
      return formatSizes(value);
    }
  }
};
</script>

<style scoped>
.agent-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 24, 40, .45);
  padding: 18px;
}
.agent-modal {
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 36px);
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(16, 24, 40, .24);
}
.agent-kb-modal {
  display: flex;
  flex-direction: column;
}
.agent-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #edf1f6;
  padding: 16px 18px;
}
.agent-modal-kicker {
  display: block;
  color: #e77f25;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.agent-modal-head h5 {
  margin: 3px 0 0;
  color: #172033;
  font-size: 18px;
  font-weight: 800;
}
.agent-modal-subtitle {
  margin: 3px 0 0;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-modal-close {
  border: 0;
  background: transparent;
  color: #98a2b3;
  font-size: 18px;
  line-height: 1;
  padding: 4px;
  cursor: pointer;
}
.agent-modal-body {
  min-height: 0;
  overflow-y: auto;
  padding: 16px 18px;
}
.agent-modal-foot {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #edf1f6;
  padding: 12px 18px;
}
.agent-kb-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e7eaf0;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 12px;
}
.agent-kb-summary strong {
  display: block;
  color: #344054;
  font-size: 13px;
}
.agent-kb-summary span {
  display: block;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.45;
}
.agent-kb-limit-note {
  margin-top: 10px;
  border: 1px solid #fde3b7;
  border-radius: 9px;
  background: #fffaf0;
  color: #8a5a00;
  padding: 9px 10px;
  font-size: 12px;
  line-height: 1.5;
}
.agent-kb-upload {
  margin-top: 12px;
  border: 1px solid #e7eaf0;
  border-radius: 10px;
  background: #fff;
  padding: 12px;
}
.agent-kb-upload-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.agent-kb-upload-head strong {
  color: #344054;
  font-size: 13px;
}
.agent-kb-file-picker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.agent-kb-file-picker small {
  color: #98a2b3;
  font-size: 12px;
}
.agent-kb-selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #edf1f6;
  border-radius: 8px;
  padding: 9px 10px;
}
.agent-kb-selected-file > div {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #475467;
  font-size: 12px;
}
.agent-kb-selected-file strong {
  overflow-wrap: anywhere;
}
.agent-kb-selected-file small {
  color: #98a2b3;
}
.agent-kb-progress {
  height: 6px;
  margin-top: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef2f6;
}
.agent-kb-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: #fe9b44;
  transition: width .2s ease;
}
.agent-kb-upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
.agent-kb-state,
.agent-kb-empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 6px;
  padding: 24px 10px;
  color: #7b8ba3;
  font-size: 12px;
  text-align: center;
}
.agent-kb-state.is-error {
  color: #c02626;
}
.agent-kb-empty i {
  color: #c7d0dc;
  font-size: 28px;
}
.agent-kb-empty strong {
  color: #475467;
  font-size: 13px;
}
.agent-kb-list {
  margin-top: 12px;
  border: 1px solid #edf1f6;
  border-radius: 10px;
  overflow: hidden;
}
.agent-kb-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #f1f4f8;
}
.agent-kb-row:last-child {
  border-bottom: 0;
}
.agent-kb-row-main {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.agent-kb-file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f5f8fc;
  color: #667085;
}
.agent-kb-row-main strong {
  display: block;
  color: #344054;
  font-size: 12px;
  overflow-wrap: anywhere;
}
.agent-kb-row-main small {
  display: block;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-kb-row-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 2px;
}
.agent-kb-row-actions .btn-link {
  padding: 3px 5px;
  font-size: 12px;
}
.agent-kb-status {
  border-radius: 999px;
  background: #fff4e5;
  color: #b76e00;
  padding: 3px 7px;
  font-size: 12px;
  font-weight: 800;
}
.agent-kb-status.is-success {
  background: #e7f8ef;
  color: #0b8f4d;
}
.agent-kb-status.is-processing {
  background: #e8f2ff;
  color: #1769d1;
}
.agent-kb-status.is-failed {
  background: #fff0f0;
  color: #c02626;
}
.agent-modal-note {
  margin-top: 10px;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
}
.agent-modal-note.is-error {
  background: #fff7f7;
  color: #c02626;
}
.agent-modal-note.is-success {
  background: #f0fdf4;
  color: #0b8f4d;
}
.agent-modal-note.is-warning {
  background: #fffaf0;
  color: #8a5a00;
}
.agent-kb-confirm {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 12px;
  border: 1px solid #f5c2c0;
  border-radius: 10px;
  background: #fff7f7;
  padding: 11px 12px;
}
.agent-kb-confirm-copy {
  min-width: 0;
}
.agent-kb-confirm-copy strong {
  display: block;
  color: #b42318;
  font-size: 12px;
}
.agent-kb-confirm-copy span {
  display: block;
  margin-top: 2px;
  color: #667085;
  font-size: 12px;
  line-height: 1.45;
}
.agent-kb-confirm-copy small {
  display: block;
  margin-top: 5px;
  color: #b42318;
  font-size: 12px;
}
.agent-kb-confirm-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 7px;
}
.agent-kb-detail {
  margin-top: 12px;
  border: 1px solid #e7eaf0;
  border-radius: 10px;
  background: #fbfcfe;
  padding: 11px;
}
.agent-kb-detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
}
.agent-kb-detail-head strong {
  color: #344054;
  font-size: 12px;
}
.agent-kb-detail > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  color: #475467;
  font-size: 12px;
}
.agent-kb-detail > div span {
  color: #98a2b3;
}
.agent-kb-detail > div strong {
  max-width: 70%;
  text-align: right;
  overflow-wrap: anywhere;
}
@media (max-width: 575px) {
  .agent-modal-backdrop {
    padding: 8px;
  }
  .agent-modal {
    max-height: calc(100vh - 16px);
  }
  .agent-kb-summary,
  .agent-kb-confirm,
  .agent-kb-row {
    align-items: flex-start;
    flex-direction: column;
  }
  .agent-kb-summary .btn {
    width: 100%;
  }
  .agent-kb-confirm-actions {
    width: 100%;
  }
  .agent-kb-confirm-actions .btn {
    flex: 1 1 auto;
  }
  .agent-kb-row-actions {
    justify-content: flex-start;
  }
}

/* Monitoring antrean Knowledge Base */
.agent-kb-status.is-danger,
.agent-kb-status.is-failed {
  background: #fff0f0;
  color: #c02626;
}
.agent-kb-status.is-warning,
.agent-kb-status.is-partial_success {
  background: #fff4e5;
  color: #b76e00;
}
.agent-kb-status.is-muted,
.agent-kb-status.is-neutral,
.agent-kb-status.is-cancelled {
  background: #f2f4f7;
  color: #667085;
}
.agent-kb-status.is-finalizing {
  background: #e8f2ff;
  color: #1769d1;
}
.agent-kb-row-progress {
  margin-top: 5px;
  max-width: 340px;
}
.agent-kb-row-progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #475467;
  font-size: 11px;
  font-weight: 700;
}
.agent-kb-row-progress-head em {
  color: #1769d1;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}
.agent-kb-progress.is-inline {
  height: 5px;
  margin-top: 4px;
}
.agent-kb-progress-bar.is-success { background: #12b76a; }
.agent-kb-progress-bar.is-danger { background: #f04438; }
.agent-kb-progress-bar.is-warning { background: #f79009; }
.agent-kb-progress-bar.is-muted,
.agent-kb-progress-bar.is-neutral { background: #98a2b3; }
.agent-kb-row-progress-meta {
  display: block;
  margin-top: 3px;
  color: #7b8ba3;
  font-size: 11px;
}
.agent-kb-row-progress-error {
  display: block;
  margin-top: 2px;
  color: #b42318;
  font-size: 11px;
  line-height: 1.4;
}
.agent-kb-detail > .agent-kb-detail-job,
.agent-kb-detail > .agent-kb-detail-muted,
.agent-kb-detail > .agent-kb-detail-legacy {
  display: block;
  padding: 0;
}
.agent-kb-detail-muted {
  margin-top: 8px;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-kb-detail-legacy {
  margin-top: 8px;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  background: #fff;
  color: #7b8ba3;
  padding: 8px 9px;
  font-size: 11px;
  line-height: 1.5;
}
.agent-kb-detail-job {
  margin-top: 9px;
  border-top: 1px solid #e7eaf0;
  padding-top: 9px !important;
}
.agent-kb-detail-job-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.agent-kb-detail .agent-kb-detail-job strong,
.agent-kb-detail .agent-kb-detail-muted strong,
.agent-kb-detail .agent-kb-detail-legacy strong {
  max-width: none;
  text-align: left;
}
.agent-kb-detail-job-head strong {
  color: #344054;
  font-size: 12px;
}
.agent-kb-detail-job-percent {
  margin-top: 5px;
  color: #475467;
  font-size: 11px;
  font-weight: 700;
}
.agent-kb-detail-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 5px;
  color: #7b8ba3;
  font-size: 11px;
}
.agent-kb-detail-counts strong {
  color: #344054;
}
.agent-kb-detail-error {
  margin: 6px 0 0;
  color: #b42318;
  font-size: 11px;
  line-height: 1.45;
}
.agent-kb-detail-chunks {
  margin-top: 8px;
  border-top: 1px solid #eef1f5;
  padding-top: 8px;
}
.agent-kb-detail-chunks-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.agent-kb-detail-chunks-head strong {
  color: #344054;
  font-size: 11px;
}
.agent-kb-detail-chunks-head .btn-link {
  padding: 2px 4px;
  font-size: 11px;
}
.agent-kb-chunk-list {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 5px;
  max-height: 220px;
  overflow-y: auto;
  margin-top: 6px;
  padding-right: 2px;
}
.agent-kb-chunk {
  border: 1px solid #eef1f5;
  border-radius: 8px;
  background: #fff;
  padding: 7px 8px;
}
.agent-kb-chunk.is-danger {
  border-color: #f5c2c0;
}
.agent-kb-chunk-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.agent-kb-chunk-head strong {
  color: #344054;
  font-size: 11px;
}
.agent-kb-chunk-preview {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 2px;
  color: #7b8ba3;
  font-size: 11px;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.agent-kb-chunk em {
  display: block;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 10px;
  font-style: normal;
}
.agent-kb-chunk-error {
  display: block;
  margin-top: 3px;
  color: #b42318;
  font-size: 10px;
  line-height: 1.4;
}
.agent-kb-chunk-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
}
.agent-kb-chunk-pager span {
  color: #7b8ba3;
  font-size: 10px;
  font-weight: 700;
}
.agent-kb-detail .agent-kb-detail-chunks .agent-kb-status,
.agent-kb-detail .agent-kb-detail-job .agent-kb-status {
  color: inherit;
}
.agent-kb-detail .agent-kb-detail-chunks .agent-kb-status.is-success,
.agent-kb-detail .agent-kb-detail-job .agent-kb-status.is-success { color: #0b8f4d; }
.agent-kb-detail .agent-kb-detail-chunks .agent-kb-status.is-processing,
.agent-kb-detail .agent-kb-detail-job .agent-kb-status.is-processing { color: #1769d1; }
.agent-kb-detail .agent-kb-detail-chunks .agent-kb-status.is-danger,
.agent-kb-detail .agent-kb-detail-job .agent-kb-status.is-danger { color: #c02626; }
@media (max-width: 575px) {
  .agent-kb-row-progress {
    max-width: none;
  }
  .agent-kb-detail-counts {
    font-size: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/components/KnowledgeBaseModal.vue